import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, FolderOpen, LayoutTemplate, Loader2, Plus } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Card, CardContent } from 'components/ui/card';
import { useIsMobile } from 'hooks/use-media-query';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import * as TestModule from 'modules/test';
import * as CollectionsModule from 'modules/collections';
import { useAuthContext } from 'modules/auth/context';
import type { IeltsScope, IeltsVariant } from '@examing/types';
import { FormatPicker, type TestFormat } from 'modules/standardizedExam/components/authoring/FormatPicker';

const NEW_COLLECTION = '__new__';

/** The chosen collection is what later attaches to teams, so their members can take the test. */
export const CreateTestRedirect: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useAuthContext();
  const organizationId = state.organization?.id;
  // Autofocus raises the keyboard on a phone and hides the formats the author has yet to choose from.
  const isMobile = useIsMobile();

  const { collections, isLoading: collectionsLoading } = CollectionsModule.Hooks.useOwned({
    enabled: Boolean(organizationId),
  });

  const createTest = TestModule.Hooks.useCreate();
  const createCollection = CollectionsModule.Hooks.useCreate();
  const addItem = CollectionsModule.Hooks.useAddItem();

  const [title, setTitle] = useState('');
  const [collectionChoice, setCollectionChoice] = useState<string>('');
  const [newCollectionName, setNewCollectionName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [format, setFormat] = useState<TestFormat>('STANDARD');
  const [ieltsVariant, setIeltsVariant] = useState<IeltsVariant>('ACADEMIC');
  const [ieltsScope, setIeltsScope] = useState<IeltsScope | undefined>(undefined);

  const creatingNew = collectionChoice === NEW_COLLECTION;
  const canSubmit = useMemo(() => {
    if (!title.trim() || !collectionChoice) return false;
    if (creatingNew && !newCollectionName.trim()) return false;
    return true;
  }, [title, collectionChoice, creatingNew, newCollectionName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationId || !canSubmit || submitting) return;
    setSubmitting(true);
    try {
      // 1. Resolve the target collection (existing or created inline).
      let collectionId = collectionChoice;
      if (creatingNew) {
        const created = await createCollection.mutateAsync({ name: newCollectionName.trim() });
        if (!created?.id) throw new Error('collection-create-failed');
        collectionId = created.id;
      }

      // 2. Create the test, then add it to the collection.
      const test = (await createTest.mutateAsync({
        title: title.trim(),
        organizationId,
        language: 'uz',
        ...(format === 'IELTS'
          ? { examConfig: { standard: 'IELTS' as const, ielts: { variant: ieltsVariant, ...(ieltsScope ? { scope: ieltsScope } : {}) } } }
          : {}),
        ...(format === 'SAT' ? { examConfig: { standard: 'SAT' as const } } : {}),
      })) as {
        id?: string;
      };
      const testId = test?.id;
      if (!testId) {
        navigate('/app/tests', { replace: true });
        return;
      }

      await addItem.mutateAsync({ collectionId, type: 'test', itemId: testId });
      // Standardized tests start on their sections; standard tests on settings.
      navigate(`/app/tests/${testId}/${format === 'STANDARD' ? 'settings' : 'questions'}`, { replace: true });
    } catch {
      // hook toasts surface the error; stay on the form
      setSubmitting(false);
    }
  };

  if (!organizationId) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-muted-foreground">
        {t('no_organization_redirecting')}
      </div>
    );
  }

  return (
    <div className="flex justify-center sm:p-6">
      {/* Phones get the form on the page itself; the card chrome returns from `sm`. */}
      <Card className="w-full max-w-lg rounded-none border-0 bg-transparent shadow-none sm:rounded-xl sm:border sm:bg-card sm:shadow-sm">
        <CardContent className="p-0 sm:p-6">
          <h1 className="text-2xl font-bold tracking-tight">{t('create_test.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('create_test.collection_help')}</p>
          <Link
            to="/app/tests/templates"
            className="mt-5 flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3.5 text-sm transition-colors hover:border-foreground/30 hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <LayoutTemplate className="h-[18px] w-[18px]" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-foreground">{t('templates.start_from_template')}</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{t('templates.start_from_template_desc')}</span>
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          </Link>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="new-test-title">{t('create_test.name_label')}</Label>
              <Input
                id="new-test-title"
                placeholder={t('create_test.name_placeholder')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus={!isMobile}
                required
              />
            </div>

            <FormatPicker
              format={format}
              onFormat={setFormat}
              ieltsVariant={ieltsVariant}
              onIeltsVariant={setIeltsVariant}
              ieltsScope={ieltsScope}
              onIeltsScope={setIeltsScope}
            />

            <div className="space-y-2">
              <Label htmlFor="new-test-collection">{t('create_test.collection_label')}</Label>
              <Select value={collectionChoice} onValueChange={setCollectionChoice}>
                <SelectTrigger id="new-test-collection">
                  <SelectValue placeholder={t('create_test.select_collection')} />
                </SelectTrigger>
                <SelectContent>
                  {collections.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={NEW_COLLECTION}>
                    <span className="flex items-center gap-2">
                      <Plus className="h-3.5 w-3.5" /> {t('create_test.new_collection')}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {collectionsLoading && (
                <p className="text-xs text-muted-foreground">{t('loading')}</p>
              )}
              {creatingNew && (
                <div className="flex items-center gap-2 pt-1">
                  <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    placeholder={t('create_test.new_collection_placeholder')}
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Pinned to the bottom edge on phones so Create is always in reach however long the form grows. */}
            <div className="sticky bottom-0 z-10 -mx-4 flex gap-3 border-t border-border bg-background/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:justify-end sm:border-0 sm:bg-transparent sm:p-0 sm:pt-2 sm:backdrop-blur-none">
              <Button type="button" variant="outline" className="flex-1 sm:flex-none" onClick={() => navigate('/app/tests')}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="flex-1 sm:flex-none" disabled={!canSubmit || submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('create_test.creating')}
                  </>
                ) : (
                  t('create_test.submit')
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateTestRedirect;
